import React, { useCallback, useRef, useState } from 'react'
import { message, Modal, Tooltip } from '@surecompanies/core_components'
import InfoCircleOutlined from '@ant-design/icons/InfoCircleOutlined'
import FileOutlined from '@ant-design/icons/FileOutlined'
import PlanAndRateOverrideForm, {
  type PlanAndRateServerFieldError,
} from './PlanAndRateOverrideForm'
import type { CreatePlanAndRateOverrideRuleRequestDto } from '../../../api/dto/plan-and-rate-override-rules.dto'
import { PlanAndRateOverrideRuleStatus } from '../enums/plan-and-rate-override-rule.enum'
import { useCreatePlanAndRateOverrideRule } from '../hooks/useCreatePlanAndRateOverrideRule'
import {
  extractFieldErrors,
  getApiErrorMessage,
  getApiErrorStatus,
} from '../../../utils/apiErrors'
import {
  presignUploadUrl,
  completeUpload,
} from '../../../api/plan-override-documents-api'
import { uploadToS3 } from '../../../utils/uploadToS3'
import DocumentDropzone from '../../../components/DocumentDropzone'
import { StagedDocumentItemCard } from '../../../components/DocumentItemCard'
import styles from '../../../components/PlanOverridesComponents.module.scss'
import {
  ALLOWED_MIME_TYPES,
  MAX_SIZE_BY_MIME,
  MAX_FILES_PER_OVERRIDE,
} from '../../../utils/fileValidation'
import { PLAN_AND_RATE_OVERRIDE_RULE_BASE } from '../../../api/plan-and-rate-override-rules-api'

interface Props {
  open: boolean
  onClose: () => void
  onCreated?: (landingStatus: PlanAndRateOverrideRuleStatus) => void
  onRequestEdit: (ruleId: string) => void
}

export default function NewPlanAndRateOverrideModal({
  open,
  onClose,
  onCreated,
  onRequestEdit,
}: Props) {
  const { mutateAsync, isPending } = useCreatePlanAndRateOverrideRule()
  const [serverErrors, setServerErrors] = useState<
    PlanAndRateServerFieldError[]
  >([])
  const [stagedFiles, setStagedFiles] = useState<File[]>([])
  const [isUploading, setIsUploading] = useState(false)
  const [isSubmitLocked, setIsSubmitLocked] = useState(false)
  const submitLockRef = useRef(false)

  const isBusy = isPending || isUploading || isSubmitLocked

  const handleClose = () => {
    if (isBusy) return
    setServerErrors([])
    setStagedFiles([])
    onClose()
  }

  const handleDuplicateFound = (ruleId: string) => {
    onClose()
    onRequestEdit(ruleId)
  }

  const handleFilesSelected = useCallback((incoming: File[]) => {
    const accepted: File[] = []
    for (const file of incoming) {
      if (!ALLOWED_MIME_TYPES.has(file.type)) {
        message.error(`${file.name}: unsupported file type.`)
        continue
      }
      const maxSize = MAX_SIZE_BY_MIME[file.type]
      if (file.size > maxSize) {
        const mb = Math.round(maxSize / (1024 * 1024))
        message.error(`${file.name}: exceeds the ${mb} MB limit.`)
        continue
      }
      accepted.push(file)
    }
    if (accepted.length === 0) return
    setStagedFiles((prev) => {
      const keys = new Set(prev.map((f) => `${f.name}|${f.size}`))
      const merged = [
        ...prev,
        ...accepted.filter((f) => !keys.has(`${f.name}|${f.size}`)),
      ]
      if (merged.length > MAX_FILES_PER_OVERRIDE) {
        message.error(
          `Maximum ${MAX_FILES_PER_OVERRIDE} files allowed per override.`
        )
        return prev
      }
      return merged
    })
  }, [])

  const handleRemoveFile = useCallback((index: number) => {
    setStagedFiles((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const handleSubmit = async (
    body: CreatePlanAndRateOverrideRuleRequestDto
  ) => {
    if (submitLockRef.current || isBusy) return
    submitLockRef.current = true
    setIsSubmitLocked(true)
    setServerErrors([])
    try {
      const created = await mutateAsync(body)
      const newRuleId = created.id

      if (stagedFiles.length === 0) {
        message.success('Plan & rate override draft saved.')
        onCreated?.(PlanAndRateOverrideRuleStatus.DRAFT)
        onClose()
        return
      }

      setIsUploading(true)
      const failures: string[] = []

      for (const file of stagedFiles) {
        try {
          const presign = await presignUploadUrl(
            newRuleId,
            {
              fileName: file.name,
              contentType: file.type,
              fileSize: file.size,
            },
            PLAN_AND_RATE_OVERRIDE_RULE_BASE
          )
          const result = await uploadToS3({
            presignedUrl: presign.presignedUploadUrl,
            file,
            contentType: file.type,
          })
          if (!result.success) {
            throw result.error ?? new Error('S3 upload failed')
          }
          await completeUpload(
            newRuleId,
            presign.documentId,
            { sizeBytes: file.size },
            PLAN_AND_RATE_OVERRIDE_RULE_BASE
          )
        } catch {
          failures.push(file.name)
        }
      }

      setIsUploading(false)

      if (failures.length === 0) {
        message.success(
          `Override + ${stagedFiles.length} file${stagedFiles.length === 1 ? '' : 's'} saved.`
        )
      } else {
        message.warning(
          `Override saved. ${failures.length} of ${stagedFiles.length} file${stagedFiles.length === 1 ? '' : 's'} failed — finish uploading in Edit.`
        )
      }

      setStagedFiles([])
      onCreated?.(PlanAndRateOverrideRuleStatus.DRAFT)
      onClose()
    } catch (err) {
      setIsUploading(false)
      const status = getApiErrorStatus(err)
      if (status === 400 || status === 422) {
        const fieldErrors = extractFieldErrors(err)
        if (fieldErrors.length > 0) {
          setServerErrors(fieldErrors)
          return
        }
        message.error(
          getApiErrorMessage(
            'Some fields look incorrect. Please review and try again.'
          )
        )
      } else {
        // eslint-disable-next-line no-console
        console.error('createPlanAndRateOverrideRule failed', err)
        message.error('An error occurred. Please try again.')
      }
    } finally {
      submitLockRef.current = false
      setIsSubmitLocked(false)
    }
  }

  return (
    <Modal
      title="New Plan & Rate Override"
      open={open}
      onCancel={handleClose}
      footer={null}
      width={960}
      destroyOnClose
      maskClosable={!isBusy}
    >
      <PlanAndRateOverrideForm
        mode="create"
        serverErrors={serverErrors}
        isSubmitting={isBusy}
        onSubmit={handleSubmit}
        onCancel={handleClose}
        onDuplicateFound={handleDuplicateFound}
        extraContent={
          <div>
            <div className={styles.documentSectionHeader}>
              <span className={styles.documentSectionLabel}>
                Evidence documents
              </span>
              <Tooltip title="Upload evidence documents (PHI/PII-free). Max 3 files per override.">
                <InfoCircleOutlined
                  className={styles.documentSectionInfoIcon}
                />
              </Tooltip>
              <span className={styles.documentSectionCount}>
                {stagedFiles.length} of {MAX_FILES_PER_OVERRIDE} max
              </span>
            </div>
            <div className={styles.documentSectionGrid}>
              <div className={styles.documentSectionUploadCol}>
                {stagedFiles.length < MAX_FILES_PER_OVERRIDE ? (
                  <DocumentDropzone
                    onFilesSelected={handleFilesSelected}
                    disabled={isBusy}
                    fileCount={stagedFiles.length}
                    maxFiles={MAX_FILES_PER_OVERRIDE}
                  />
                ) : (
                  <div className={styles.documentSectionLimitBox}>
                    <p className={styles.documentSectionLimitNote}>
                      Maximum {MAX_FILES_PER_OVERRIDE} files reached. Remove a
                      file to upload another.
                    </p>
                  </div>
                )}
              </div>
              <div className={styles.documentSectionFilesCol}>
                {stagedFiles.length === 0 ? (
                  <div className={styles.documentSectionFilesEmpty}>
                    <FileOutlined
                      className={styles.documentSectionFilesEmptyIcon}
                    />
                    <p className={styles.documentSectionFilesEmptyText}>
                      Uploaded files will appear here
                    </p>
                  </div>
                ) : (
                  stagedFiles.map((file, index) => (
                    <StagedDocumentItemCard
                      key={`${file.name}|${file.size}`}
                      file={file}
                      onRemove={() => handleRemoveFile(index)}
                      disabled={isBusy}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        }
      />
    </Modal>
  )
}
